import type {
  ContentSurface,
  PublicContentBlock,
  PublicContentSurfaceResponse,
} from '@opinix-cloud/shared';
import { DEFAULT_SURFACES } from './content-defaults';

/**
 * Fetch + cache helper for the marketing-pages CMS
 * (FEAT-SAAS-MKT-004). Strategy:
 *
 *   1. Synchronously read a localStorage cache (max age 5 min) so the
 *      first paint can use the freshest server copy when available.
 *   2. Issue a network fetch to /api/content/:surface in the
 *      background; on success, update state + the cache so subsequent
 *      visits are instant.
 *   3. On any failure (404, network down, malformed JSON) fall back to
 *      the bundled defaults that ship with the SPA. The site never
 *      appears empty.
 *
 * Visual fidelity: when no edits have been made, both the bundled
 * defaults AND the server response (which is the seeded copy of those
 * defaults) produce the same rendered output. When the operator edits
 * a block, the fetch returns the new payload and the section renders
 * the override.
 */

const CACHE_PREFIX = 'opinix.content.';
const CACHE_MAX_AGE_MS = 5 * 60 * 1000;

interface CacheEntry {
  ts: number;
  blocks: PublicContentBlock[];
}

export function getDefaultBlocks(
  surface: ContentSurface,
): PublicContentBlock[] {
  const def = DEFAULT_SURFACES.find((s) => s.surface === surface);
  if (!def) return [];
  return def.blocks.map((b, i) => ({
    key: b.key,
    position: i,
    blockType: b.blockType,
    payload: b.payload,
  }));
}

export function readCache(surface: ContentSurface): PublicContentBlock[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + surface);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (Date.now() - parsed.ts > CACHE_MAX_AGE_MS) return null;
    return parsed.blocks;
  } catch {
    return null;
  }
}

function writeCache(surface: ContentSurface, blocks: PublicContentBlock[]): void {
  if (typeof window === 'undefined') return;
  try {
    const entry: CacheEntry = { ts: Date.now(), blocks };
    window.localStorage.setItem(CACHE_PREFIX + surface, JSON.stringify(entry));
  } catch {
    /* quota / disabled — ignore */
  }
}

export async function fetchSurface(
  surface: ContentSurface,
): Promise<PublicContentBlock[]> {
  try {
    const res = await fetch(`/api/content/${surface}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      console.info(
        `[content] /api/content/${surface} returned ${res.status}, using defaults`,
      );
      return getDefaultBlocks(surface);
    }
    const body = (await res.json()) as PublicContentSurfaceResponse;
    if (!Array.isArray(body?.blocks) || body.blocks.length === 0) {
      console.info(`[content] empty payload for ${surface}, using defaults`);
      return getDefaultBlocks(surface);
    }
    writeCache(surface, body.blocks);
    return body.blocks;
  } catch (err) {
    console.info(`[content] fetch failed for ${surface}`, err);
    return getDefaultBlocks(surface);
  }
}

/**
 * Find a block by key; falls back to the matching default if the
 * server omitted it (e.g., admin only edited some blocks). Type
 * narrowing happens at the call site.
 */
export function findBlock(
  blocks: PublicContentBlock[],
  key: string,
): PublicContentBlock | undefined {
  return blocks.find((b) => b.key === key);
}

export function getBlockPayload<T = unknown>(
  blocks: PublicContentBlock[],
  surface: ContentSurface,
  key: string,
): T {
  const found = findBlock(blocks, key);
  if (found) return found.payload as T;
  const fallback = getDefaultBlocks(surface).find((b) => b.key === key);
  return (fallback?.payload ?? {}) as T;
}
